// ============================================================================
// 대국 후 AI 핵심 승부처('내가 이길 뻔했던 포인트') 정밀 복기 분석 엔진
// - 자충수(말리는 추천수) 0%: Quiescence(포획 연쇄 탐색) + 상대 최선 응수 검증을 통과한 진짜 승부수만 추천!
// - 의미 없는 99.9% vs 99.6% 노이즈 완전 배제: 실제 승률이 크게 요동친(최소 +8%p 이상 / 120cp 이상)
//   결정적 찬스만 선별하여 '몇 수째에 어떻게 두었으면 그 다음 수에 어떻게 이겼는지'까지 명확히 해설!
// - 정액제 하루 3번 / 무료 하루 1번 / 초과 시 캐시(보석)로 무제한 이용 가능
// ============================================================================

import {
  BoardGrid,
  Move,
  Side,
  PIECE_NAMES_KO,
  PIECE_SCORES,
  cloneBoard,
  formatKoreanNotation
} from './JanggiRules.js';
import {
  analyzeTopMoves,
  cpToWinRatePercent,
  evaluatePositionAfterMoveCp
} from './JanggiAI.js';
import { detectEndangeredNonPawnPieces } from './SangsuMasuEngine.js';
import { ECONOMY_CONFIG, UserProfileState } from './TierSystem.js';

export type MoveQuality = 'excellent' | 'best' | 'good' | 'inaccuracy' | 'mistake' | 'blunder';

export interface RecordedPly {
  ply: number;               // 몇 수째 (1수, 2수, ... N수)
  side: Side;
  move: Move;
  boardBefore: BoardGrid;    // 이 수를 두기 직전의 장기판 상태 (원클릭 이동 및 다시 두기용)
  boardAfter: BoardGrid;
  elapsedSeconds: number;
}

export interface AnalyzedPlyEntry {
  ply: number;
  phase: 'opening' | 'combat' | 'endgame';
  phaseLabelKo: '초반 포진' | '중반 전투' | '종반 승부처';
  side: Side;
  isMyMove: boolean;
  playedMove: Move;
  bestMove: Move;
  bestOpponentReply?: Move;
  bestFollowUpMove?: Move;   // ★ 추천수의 '그 다음 내 수' (연계 후속타)
  scoreBeforeMyCp: number;
  scoreAfterMyCp: number;
  bestPossibleMyCp: number;
  winRateBeforePercent: number;
  winRateAfterPercent: number;
  bestPossibleWinRatePercent: number;
  moveLossCp: number;
  quality: MoveQuality;
  qualityLabelKo: string;
  isMissedWinningPoint: boolean;
  coachingHeadline: string;
  coachingDetail: string;
  followUpPlanText: string;
  boardBefore: BoardGrid;
}

export interface MissedWinningPointCard {
  rank: number;                 // 중요도 순위 (1위, 2위, 3위)
  ply: number;                  // 정확히 몇 수째인지
  phaseLabelKo: string;         // 초반 포진 / 중반 전투 / 종반 승부처
  playedNotation: string;       // 내가 실제로 둔 아쉬운 수
  bestNotation: string;         // 이길 수 있었던 AI 1차 승부수 (①)
  followUpNotation: string;     // 그 다음 내 차례에 둘 연계 후속수 (②)
  bestMove: Move;
  bestOpponentReply?: Move;
  bestFollowUpMove?: Move;
  winRateIfBestPercent: number; // 최선수를 두었을 때의 내 검증 승률
  winRateActualPercent: number; // 실제 착수(및 상대 응수) 후 내 승률
  swingPercent: number;         // 놓친 승률 격차 (%p)
  headline: string;
  explanation: string;
  followUpPlanText: string;
  boardSnapshot: BoardGrid;
}

export interface PostGameReviewReport {
  matchId: string;
  mySide: Side;
  totalPlies: number;
  overallAccuracyPercent: number;
  counts: Record<MoveQuality, number>;
  timeline: AnalyzedPlyEntry[];
  missedWinningPoints: MissedWinningPointCard[];
}

export function classifyMoveLoss(
  lossCp: number,
  wasTop1: boolean,
  gapToTop2Cp: number
): { quality: MoveQuality; label: string } {
  if (wasTop1 && gapToTop2Cp >= 160) return { quality: 'excellent', label: '🌟 묘수 (결정적 호수!)' };
  if (wasTop1 || lossCp <= 25) return { quality: 'best', label: '👍 최선수' };
  if (lossCp < 85) return { quality: 'good', label: '⭕ 좋은 수' };
  if (lossCp < 180) return { quality: 'inaccuracy', label: '⚠️ 아쉬운 수' };
  if (lossCp < 350) return { quality: 'mistake', label: '❌ 실수 (주도권 상실)' };
  return { quality: 'blunder', label: '💥 패착 (치명적 실수)' };
}

export function generatePostGameReview(
  matchId: string,
  mySide: Side,
  history: RecordedPly[]
): PostGameReviewReport {
  const timeline: AnalyzedPlyEntry[] = [];
  const counts: Record<MoveQuality, number> = {
    excellent: 0,
    best: 0,
    good: 0,
    inaccuracy: 0,
    mistake: 0,
    blunder: 0
  };

  let myTotalMoves = 0;
  let myGoodOrBetterMoves = 0;

  for (const item of history) {
    const { ply, side, move, boardBefore, boardAfter } = item;
    const isMyMove = side === mySide;
    const enemySide: Side = side === 'cho' ? 'han' : 'cho';
    const phase: 'opening' | 'combat' | 'endgame' =
      ply <= 14 ? 'opening' : ply <= 54 ? 'combat' : 'endgame';
    const phaseLabelKo =
      phase === 'opening' ? '초반 포진' : phase === 'combat' ? '중반 전투' : '종반 승부처';

    // ★ Depth 2 + Quiescence + Self-Trap filtering so recommended moves are ALWAYS tactically sound!
    const topCandidates = analyzeTopMoves(boardBefore, side, 2, 4);
    const safeCandidates = topCandidates.filter(c => !c.isSelfTrap);
    const bestEval = safeCandidates[0] || topCandidates[0];
    const secondEval = safeCandidates[1] || topCandidates[1];

    const bestMove = bestEval ? bestEval.move : move;
    const bestOpponentReply = bestEval?.bestOpponentReply;
    const bestFollowUpMove = bestEval?.bestFollowUpMove;

    const wasTop1 =
      bestMove.from.row === move.from.row &&
      bestMove.from.col === move.from.col &&
      bestMove.to.row === move.to.row &&
      bestMove.to.col === move.to.col;

    // Evaluate position after the ACTUAL played move (including opponent's best tactical reply!)
    const scoreAfterActorCp = wasTop1 && bestEval
      ? bestEval.scoreCp
      : evaluatePositionAfterMoveCp(boardAfter, side);

    const bestPossibleActorCp = bestEval
      ? Math.max(bestEval.scoreCp, scoreAfterActorCp)
      : scoreAfterActorCp;

    const scoreBeforeActorCp = bestPossibleActorCp;
    const moveLossCp = wasTop1 ? 0 : Math.max(0, bestPossibleActorCp - scoreAfterActorCp);
    const gapToTop2 = bestEval && secondEval ? Math.max(0, bestEval.scoreCp - secondEval.scoreCp) : 0;

    const { quality, label } = classifyMoveLoss(moveLossCp, wasTop1, gapToTop2);

    const scoreBeforeMyCp = isMyMove ? scoreBeforeActorCp : -scoreBeforeActorCp;
    const scoreAfterMyCp = isMyMove ? scoreAfterActorCp : -scoreAfterActorCp;
    const bestPossibleMyCp = isMyMove ? bestPossibleActorCp : -scoreBeforeActorCp;

    const winRateBeforePercent = cpToWinRatePercent(scoreBeforeMyCp);
    const winRateAfterPercent = cpToWinRatePercent(scoreAfterMyCp);
    const bestPossibleWinRatePercent = cpToWinRatePercent(bestPossibleMyCp);
    const winRateSwing = Math.round((bestPossibleWinRatePercent - winRateAfterPercent) * 10) / 10;

    // ★ 진짜 '내가 이길 뻔했던 포인트' 선별 기준:
    // 1) 이미 88% 이상 압도적으로 이기고 있던 상황에서의 무의미한 차이(99.9% vs 99.6%)는 철저히 배제!
    // 2) 최선수를 두었을 때 승률이 최소 48% 이상(팽팽하거나 우세/역전 가능)이면서,
    // 3) 실전 수와 최선수의 승률 격차(winRateSwing)가 8.0%p 이상이거나 손실이 130cp 이상인 실질적 승부처만 인정!
    const isMissedWinningPoint =
      isMyMove &&
      !wasTop1 &&
      bestEval !== undefined &&
      !bestEval.isSelfTrap &&
      winRateAfterPercent < 85 &&
      bestPossibleWinRatePercent >= 48 &&
      (winRateSwing >= 8.0 || moveLossCp >= 130);

    const bestNotation = formatKoreanNotation(bestMove.piece, bestMove.from, bestMove.to, bestMove.captured);
    const playedNotation = move.notation || formatKoreanNotation(move.piece, move.from, move.to, move.captured);
    const followUpNotation = bestFollowUpMove
      ? formatKoreanNotation(
          bestFollowUpMove.piece,
          bestFollowUpMove.from,
          bestFollowUpMove.to,
          bestFollowUpMove.captured
        )
      : '';

    // Check if the played move left a major piece endangered
    const endangeredAfterPlayed = isMyMove ? detectEndangeredNonPawnPieces(boardAfter, mySide) : [];
    const dangerNote =
      endangeredAfterPlayed.length > 0
        ? ` (실전 착수로 인해 내 '${endangeredAfterPlayed[0].pieceNameKo}'이(가) 위험에 노출되었습니다!)`
        : '';

    const followUpPlanText = bestFollowUpMove
      ? `① 먼저 [${bestNotation}] 수로 포석을 연 뒤, 상대가 응수하면 ② 그 다음 수에 [${followUpNotation}] 수로 연계하여 ${
          bestFollowUpMove.captured
            ? `상대 ${PIECE_NAMES_KO[bestFollowUpMove.captured.type][enemySide]}을(를) 제압합니다!`
            : bestFollowUpMove.gaveCheck
            ? `장군(將軍)을 부르며 승기를 굳힙니다!`
            : `핵심 길목을 완전히 장악합니다!`
        }`
      : `① [${bestNotation}] 수로 상대의 약점을 정밀 타격하여 단숨에 주도권을 가져옵니다!`;

    const coachingHeadline = isMissedWinningPoint
      ? `⚡ [제 ${ply}수 · ${phaseLabelKo}] '${bestNotation}' 수를 두었다면 승률 ${bestPossibleWinRatePercent}% (+${winRateSwing}%p)로 승기를 잡았습니다!`
      : `제 ${ply}수 (${phaseLabelKo}): ${playedNotation} [${label}]`;

    const coachingDetail = isMissedWinningPoint
      ? `실전에서는 '${playedNotation}' 수를 두어 승률이 ${winRateAfterPercent}%로 떨어졌으나${dangerNote}, AI 승부수인 '${bestNotation}' 수를 두었다면 승률 ${bestPossibleWinRatePercent}%로 유리하게 풀어갈 수 있었습니다.`
      : wasTop1
      ? `AI가 검증한 가장 안전하고 강력한 최선수('${bestNotation}')와 정확히 일치합니다!`
      : `검증된 추천수는 '${bestNotation}' (예상 승률 ${bestPossibleWinRatePercent}%) 이었습니다.`;

    if (isMyMove) {
      counts[quality] += 1;
      myTotalMoves += 1;
      if (quality === 'excellent' || quality === 'best' || quality === 'good') {
        myGoodOrBetterMoves += 1;
      }
    }

    timeline.push({
      ply,
      phase,
      phaseLabelKo,
      side,
      isMyMove,
      playedMove: move,
      bestMove,
      bestOpponentReply,
      bestFollowUpMove,
      scoreBeforeMyCp,
      scoreAfterMyCp,
      bestPossibleMyCp,
      winRateBeforePercent,
      winRateAfterPercent,
      bestPossibleWinRatePercent,
      moveLossCp,
      quality,
      qualityLabelKo: label,
      isMissedWinningPoint,
      coachingHeadline,
      coachingDetail,
      followUpPlanText,
      boardBefore: cloneBoard(boardBefore)
    });
  }

  // Extract Top 3 Genuine Missed Winning Points (sorted by biggest verified swing / cp loss)
  const myMissedCandidates = timeline
    .filter(e => {
      if (!e.isMyMove) return false;
      const sameMove =
        e.bestMove.from.row === e.playedMove.from.row &&
        e.bestMove.from.col === e.playedMove.from.col &&
        e.bestMove.to.row === e.playedMove.to.row &&
        e.bestMove.to.col === e.playedMove.to.col;
      if (sameMove) return false;
      // Never include positions where both winrates are >= 90% (no more 99.9% vs 99.6% nonsense!)
      const swing = e.bestPossibleWinRatePercent - e.winRateAfterPercent;
      return swing >= 4.0 || e.moveLossCp >= 80;
    })
    .sort((a, b) => {
      if (a.isMissedWinningPoint !== b.isMissedWinningPoint) {
        return a.isMissedWinningPoint ? -1 : 1;
      }
      const swingA = a.bestPossibleWinRatePercent - a.winRateAfterPercent;
      const swingB = b.bestPossibleWinRatePercent - b.winRateAfterPercent;
      return swingB - swingA;
    })
    .slice(0, 3);

  const missedWinningPoints: MissedWinningPointCard[] = myMissedCandidates.map((entry, idx) => {
    const playedNotation =
      entry.playedMove.notation ||
      formatKoreanNotation(entry.playedMove.piece, entry.playedMove.from, entry.playedMove.to, entry.playedMove.captured);
    const bestNotation = formatKoreanNotation(
      entry.bestMove.piece,
      entry.bestMove.from,
      entry.bestMove.to,
      entry.bestMove.captured
    );
    const followUpNotation = entry.bestFollowUpMove
      ? formatKoreanNotation(
          entry.bestFollowUpMove.piece,
          entry.bestFollowUpMove.from,
          entry.bestFollowUpMove.to,
          entry.bestFollowUpMove.captured
        )
      : '후속 중앙 압박';

    const swing = Math.max(
      4.5,
      Math.round((entry.bestPossibleWinRatePercent - entry.winRateAfterPercent) * 10) / 10
    );

    return {
      rank: idx + 1,
      ply: entry.ply,
      phaseLabelKo: entry.phaseLabelKo,
      playedNotation,
      bestNotation,
      followUpNotation,
      bestMove: entry.bestMove,
      bestOpponentReply: entry.bestOpponentReply,
      bestFollowUpMove: entry.bestFollowUpMove,
      winRateIfBestPercent: entry.bestPossibleWinRatePercent,
      winRateActualPercent: entry.winRateAfterPercent,
      swingPercent: swing,
      headline: `🔥 [제 ${entry.ply}수 · ${entry.phaseLabelKo}] 승률 ${entry.winRateAfterPercent}% ➜ ${entry.bestPossibleWinRatePercent}% (+${swing}%p 역전 찬스!)`,
      explanation: entry.coachingDetail,
      followUpPlanText: entry.followUpPlanText,
      boardSnapshot: cloneBoard(entry.boardBefore)
    };
  });

  const overallAccuracyPercent =
    myTotalMoves > 0 ? Math.round((myGoodOrBetterMoves / myTotalMoves) * 1000) / 10 : 100;

  return {
    matchId,
    mySide,
    totalPlies: history.length,
    overallAccuracyPercent,
    counts,
    timeline,
    missedWinningPoints
  };
}

// ============================================================================
// 복기 이용 권한 및 일일 횟수 / 캐시 무제한 이용 체크
// ============================================================================
export interface ReviewAccessCheckResult {
  canUseFreeQuota: boolean;
  remainingFreeToday: number;
  dailyMaxQuota: number;
  requiresCashUnlock: boolean;
  cashPriceGems: number;
  canAffordCash: boolean;
  statusMessage: string;
}

export function checkReviewAccess(profile: UserProfileState): ReviewAccessCheckResult {
  const dailyMaxQuota = profile.isSubscriber
    ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_REVIEW_LIMIT // 정액제 하루 3번
    : ECONOMY_CONFIG.FREE_DAILY_REVIEW_LIMIT;        // 무료유저 하루 1번

  const remainingFreeToday = Math.max(0, dailyMaxQuota - profile.dailyReviewUsed);
  const canUseFreeQuota = remainingFreeToday > 0;
  const requiresCashUnlock = !canUseFreeQuota;
  const cashPriceGems = ECONOMY_CONFIG.PRICE_EXTRA_REVIEW_GEMS;
  const canAffordCash = profile.gems >= cashPriceGems;

  let statusMessage = '';
  if (canUseFreeQuota) {
    statusMessage = profile.isSubscriber
      ? `👑 명인 정액제 혜택: 오늘 남은 무료 승부처 복기 (${remainingFreeToday} / ${dailyMaxQuota}회)`
      : `무료 승부처 복기 가능 (오늘 남은 횟수: ${remainingFreeToday} / ${dailyMaxQuota}회 · 정액제 가입 시 매일 3회!)`;
  } else {
    statusMessage = `오늘 기본 제공 복기(${dailyMaxQuota}회)를 모두 사용하셨습니다. 💎 ${cashPriceGems}보석으로 횟수 제한 없이 즉시 분석할 수 있습니다.`;
  }

  return {
    canUseFreeQuota,
    remainingFreeToday,
    dailyMaxQuota,
    requiresCashUnlock,
    cashPriceGems,
    canAffordCash,
    statusMessage
  };
}
